import {
  useUserById,
  useEditUser,
  useDeleteUser,
  useToggleUserDisabled,
  useLoginInfoByUserId,
} from "../../Query/useUsers";
import { useCertifications } from "../../Query/useCertifications";
import { useWorkspace, useWorkspaces } from "../../Query/useWorkspace";
import { useNavigate, useParams } from "react-router-dom";
import { useLoginAsAdmin } from "../../Query/useAuth";
import { useDocumentChunks } from "../../Query/useDocuments";
import {
  CircularProgress,
  FormControl,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Button,
  useMediaQuery,
  TablePagination,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import color from "../../Constants/colors";
import {
  DocumentType,
  ProcessingStatus,
  Features,
} from "../../Enums/companyStatus";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import React from "react";
import QuestionListsTable from "./QuestionListTable";
import { Certification } from "../../Models/certification";
import { FormGroup, FormControlLabel, Checkbox } from "@mui/material";
import FeaturesComponent from "./FeaturesComponent";
import { Expert } from "../../Models/expert";
import theme from "../../Constants/theme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Loading from "../Common/Loading";
import { useIpLocation } from "../../Hooks/useIpLocation";
import { getGoogleMapEmbedUrl } from "../../Constants/googleMaps";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { formatDate, formatLoginTime } from "../../Helpers/dateHelpers";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import {
  formatUTC,
  loginMethodToString,
  statusToStringWebsites,
} from "../../Helpers/stringHelpers";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { clearStorage } from "../../Helpers/clearStorage";
import { User, UserCompany } from "../../Models/user";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import { userEvaluationRequestsParams } from "../../Models/userEvaluationRequests";
import { useUserEvaluationRequests } from "../../Query/useUserEvaluationRequests";
import { useTableParams } from "../../Core/useTableParams";
import LogCard from "../Common/LogCard/LogCard";
import WorkspaceSelector from "./WorkspaceSelector";
import ModelAccessSelector from "../Common/ModelAccessSelector";
import DownloadIcon from "@mui/icons-material/Download";
import {
  useDownloadDocument,
  useDownloadWebsiteContent,
} from "../../Query/useCompanies";
import LogViewerModal from "../FileUpload/LogViewer";
import ComoAgentSelector from "../ComoAgents/ComoAgentSelector";
import colors from "../../Constants/colors";
import { ComoInput } from "../Common/ComoInput";
import UsageWidget from "../Usage/UsageWidget";
import { Avatar, Menu, MenuItem } from "@mui/material";
import {
  BadgeOutlined,
  BlockRounded,
  BusinessOutlined,
  CheckCircleOutlineRounded,
  DeleteOutlineRounded,
  DescriptionOutlined,
  EditOutlined,
  GroupsOutlined,
  HistoryRounded,
  LanguageOutlined,
  LoginRounded,
  MoreVertRounded,
  PersonOutlineRounded,
  SaveOutlined,
  SmartToyOutlined,
  TuneOutlined,
  VerifiedOutlined,
  WorkspacesOutlined,
} from "@mui/icons-material";
import {
  ProfileCard,
  StatusPill,
  StatusPillTone,
  profileBodyCellSx,
  profileButtonSx,
  profileEmptyCellSx,
  profileHeadCellSx,
  profileTableContainerSx,
  profileTableRowSx,
} from "../Profile/ProfileSection";
import { sidebarMenuPaperSx } from "../DashboardMenu/DashboardMenu";

const processingTone = (status: number): StatusPillTone => {
  switch (status) {
    case ProcessingStatus.PROCESSED:
      return "success";
    case ProcessingStatus.IN_PROGRESS:
      return "info";
    case ProcessingStatus.ERROR:
    case ProcessingStatus.TIME_LIMIT_EXCEEDED:
      return "danger";
    default:
      return "neutral";
  }
};

const UserProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("userProfilePage");
  const { userId } = useParams();
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [evaluationLimit, setEvaluationLimit] = useState(0);
  const [maxCompanyLimit, setMaxCompanyLimit] = useState(0);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [loginAsUserDialogOpen, setLoginAsUserDialogOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [certCategory, setCertCategory] = useState<Certification[]>([]);
  const [modelIds, setModelIds] = useState<number[]>([]);
  const { data: user, isLoading } = useUserById(userId ?? "");
  const { data: loginInfo } = useLoginInfoByUserId(userId);
  const { data: certCategories } = useCertifications();
  const { data: workspacesData } = useWorkspaces(1, 50);
  const editUserMutation = useEditUser();
  const deleteUserMutation = useDeleteUser();
  const downloadDocumentMutation = useDownloadDocument();
  const downloadWebsiteContentMutation = useDownloadWebsiteContent();
  const toggleUserDisabledMutation = useToggleUserDisabled();
  const loginAsAdminMutation = useLoginAsAdmin();
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const applyWorkspaceDefaults = useRef(false);
  const [features, setFeatures] = useState<number[]>([]);
  const [, setSelectedExperts] = useState<Expert[]>([]);
  const [selectedComoAgentIds, setSelectedComoAgentIds] = useState<number[]>(
    [],
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPassword, setShowPassword] = useState(false);
  const { colors: workspace } = useWorkspaceColors();
  const { page, setPage } = useTableParams({ defaultPage: 1 });
  const [chunksDialogOpen, setChunksDialogOpen] = useState(false);
  const [chunksDocumentId, setChunksDocumentId] = useState<number | null>(null);
  const [storageLimit, setStorageLimit] = useState<number>(
    user?.storage_limit ?? 0,
  );
  const [libraryLimit, setLibraryLimit] = useState<number>(
    user?.library_limit ?? 0,
  );
  const [showLockedFeatures, setShowLockedFeatures] = useState<boolean>(
    user?.show_locked_features ?? false,
  );
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    number | undefined
  >(undefined);
  const disableActions = toggleUserDisabledMutation.isPending || isEditable;

  const {
    data: chunksPayload,
    isLoading: chunksLoading,
    error: chunksError,
  } = useDocumentChunks(chunksDocumentId, chunksDialogOpen);
  const PAGE_SIZE = 20;
  const queryParams: userEvaluationRequestsParams = useMemo(() => {
    return {
      page,
      user_id: userId,
    };
  }, [page, userId]);
  const { ipLocation, loading: ipLoading, fetchIpLocation } = useIpLocation();

  const { data: workspaceResponse } = useWorkspace(
    workspaceId ? String(workspaceId) : null,
  );

  useEffect(() => {
    if (!workspaceResponse?.workspace || !applyWorkspaceDefaults.current)
      return;
    applyWorkspaceDefaults.current = false;

    const ws = workspaceResponse.workspace;

    setEvaluationLimit(ws.user_evaluation_limit ?? 0);
    setMaxCompanyLimit(ws.user_company_limit ?? 0);
    setStorageLimit(ws.user_storage_limit ?? 0);
    setLibraryLimit(ws.user_library_limit ?? 0);

    setModelIds(ws.accessible_model_ids ?? []);

    setFeatures(workspaceResponse.permissions?.map((p) => p.feature.id) ?? []);

    setShowLockedFeatures(ws.show_locked_features ?? false);

    setCertCategory([]);
    setSelectedExperts([]);
    setSelectedComoAgentIds([]);
  }, [workspaceResponse]);

  useEffect(() => {
    if (loginInfo?.data?.last_login_ip_address) {
      fetchIpLocation(loginInfo.data.last_login_ip_address);
    }
  }, [fetchIpLocation, loginInfo?.data?.last_login_ip_address]);

  const { data: logData } = useUserEvaluationRequests(queryParams);
  const queryClient = useQueryClient();

  const createHashMapCompany = (companies: UserCompany[]) => {
    const hashMap: Record<string, string> = {};
    if (!companies) return;
    companies.forEach((company) => {
      hashMap[company.id] = company.name;
    });
    return hashMap;
  };

  const companies = useMemo(
    () => createHashMapCompany(user?.companies || []),
    [user?.companies],
  );

  // Fills the form from the saved user; used on load and when an edit is cancelled.
  const fillFormFromUser = useCallback((savedUser: User) => {
    setName(savedUser.name ?? "");
    setSurname(savedUser.surname ?? "");
    setEmail(savedUser.email ?? "");
    setEvaluationLimit(savedUser.evaluation_limit ?? 0);
    setMaxCompanyLimit(savedUser.companies_limit ?? 0);
    setModelIds(savedUser.accessible_model_ids ?? []);
    setCertCategory(savedUser.certifications ?? []);
    setSelectedExperts(savedUser.experts ?? []);
    setSelectedComoAgentIds(savedUser.agent_ids ?? []);
    setStorageLimit(savedUser.storage_limit);
    setLibraryLimit(savedUser.library_limit);
    setShowLockedFeatures(savedUser.show_locked_features ?? false);
    const extractedFeatures = savedUser.permissions.map(
      (permission) => permission.id,
    );
    setFeatures(extractedFeatures);
    setWorkspaceId(savedUser.workspace?.id ?? null);
  }, []);

  useEffect(() => {
    if (user) {
      fillFormFromUser(user);
    }
  }, [user, fillFormFromUser]);

  const handleCancelEdit = () => {
    if (user) fillFormFromUser(user);
    setPassword("");
    setShowPassword(false);
    setIsEditable(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.toLowerCase());
  };

  const formattedDocuments = useMemo(() => {
    return user?.documents.map((document) => ({
      ...document,
      formattedDate: formatDate(document.created_at),
    }));
  }, [user?.documents]);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.secondary}
      />
    );
  }

  const processingStatusToString = (status: number) => {
    switch (status) {
      case ProcessingStatus.WAITING:
        return "Waiting";
      case ProcessingStatus.IN_PROGRESS:
        return "In Progress";
      case ProcessingStatus.PROCESSED:
        return "Processed";
      case ProcessingStatus.ERROR:
        return "Processing Error: Please try again";
      case ProcessingStatus.TIME_LIMIT_EXCEEDED:
        return "Error: time limit exceeded";
      default:
        return "";
    }
  };

  const documentTypeToString = (type: number) => {
    switch (type) {
      case DocumentType.TXT:
        return "Text";
      case DocumentType.WORD:
        return "Word";
      case DocumentType.PDF:
        return "PDF";
      case DocumentType.POWERPOINT:
        return "Powerpoint";
      case DocumentType.IMAGE:
        return "Image";
      default:
        return "";
    }
  };

  const companyName = (id: number) => {
    if (!companies) return "";

    return companies[id];
  };

  const handleEditUser = async () => {
    if (name.trim() === "") {
      enqueueSnackbar(t("nameCannotBeEmpty"), { variant: "warning" });
      return;
    }

    if (surname.trim() === "") {
      enqueueSnackbar(t("surnameCannotBeEmpty"), { variant: "warning" });
      return;
    }

    if (email.trim() === "") {
      enqueueSnackbar(t("emailCannotBeEmpty"), { variant: "warning" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      enqueueSnackbar(t("invalidEmail"), {
        variant: "warning",
      });
      return;
    }

    const normalizedEmail = email.toLowerCase();

    const cert_category_ids = certCategory.map((cert) => cert.id);

    editUserMutation.mutate(
      {
        userId: userId ?? "",
        name,
        surname,
        email: normalizedEmail,
        evaluationLimit,
        maxCompanyLimit,
        password,
        modelIds,
        certCategoryIds: cert_category_ids,
        workspaceId,
        features,
        selectedExpertIds: [],
        storage_limit: storageLimit,
        library_limit: libraryLimit,
        show_locked_features: showLockedFeatures,
        agent_ids: selectedComoAgentIds,
      },
      {
        onSuccess: () => {
          setIsEditable(false);
          setShowPassword(false);
        },
      },
    );
  };

  const handleConfirmationDialogOpen = () => {
    setConfirmationDialogOpen(true);
  };

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    if (confirmed) {
      handleDelete();
    } else {
      setConfirmationDialogOpen(false);
    }
  };

  const handleDialogClose = (_: unknown, reason: string) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      setConfirmationDialogOpen(false);
      return;
    }
    handleConfirmationDialogClose(false);
  };

  const handleDelete = () => {
    deleteUserMutation.mutate(userId ?? "", {
      onSuccess: () => {
        navigate("/users");
      },
    });
  };

  const handleToggleDisabled = () => {
    toggleUserDisabledMutation.mutate(userId ?? "");
  };

  const handleCertCategoryChange = (category: Certification) => {
    setCertCategory((prevCategories) => {
      if (prevCategories.some((c) => c.id === category.id)) {
        return prevCategories.filter((c) => c.id !== category.id);
      } else {
        return [...prevCategories, category];
      }
    });
  };

  const handleLoginAsUser = () => {
    loginAsAdminMutation.mutate(userId || "", {
      onSuccess: async (data) => {
        if (data?.cih_token) {
          await clearStorage([
            "cw_conversation",
            "cw_clearence",
            "cih_token",
            "persist:annex:v1",
          ]);

          localStorage.setItem("cih_token", data.cih_token);

          queryClient.removeQueries();

          window.location.href = "/home";
        }
      },
    });
  };

  const handleOpenChunks = (documentId: number) => {
    setChunksDocumentId(documentId);
    setChunksDialogOpen(true);
  };

  const handleCloseChunks = () => {
    setChunksDialogOpen(false);
    setChunksDocumentId(null);
  };

  const handleDownloadChunks = () => {
    if (!chunksPayload) return;
    const pseudoResponse = {
      data:
        typeof chunksPayload === "object"
          ? JSON.stringify(chunksPayload, null, 2)
          : chunksPayload,
    } as unknown as AxiosResponse;
    downloadDocumentBlob(
      pseudoResponse,
      `document-${chunksDocumentId ?? "unknown"}-chunks.txt`,
    );
  };

  const handleDownloadDocument = (documentId: number, fileName: string) => {
    downloadDocumentMutation.mutate(
      { documentId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, fileName);
        },
      },
    );
  };

  const handleViewLogs = (documentId: number) => {
    setSelectedDocumentId(documentId);
    setLogModalOpen(true);
  };

  const handleDownloadScrappedWebsiteContent = (
    websiteId: number,
    name: string,
  ) => {
    downloadWebsiteContentMutation.mutate(
      { websiteId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, `${name}.txt`);
        },
      },
    );
  };

  const fullName = [user?.name, user?.surname].filter(Boolean).join(" ");
  const initials =
    [user?.name, user?.surname]
      .map((part) => part?.trim().charAt(0) ?? "")
      .join("")
      .toUpperCase() || (user?.email ?? "?").charAt(0).toUpperCase();

  const loginRows = loginInfo?.data
    ? [
        { label: t("id"), value: loginInfo.data.id },
        { label: t("userId"), value: loginInfo.data.user_id },
        { label: t("createdAt"), value: loginInfo.data.created_at },
        { label: t("updatedAt"), value: loginInfo.data.updated_at },
        {
          label: t("lastLoginTime"),
          value: formatLoginTime(loginInfo.data.last_login_time),
        },
        {
          label: t("lastLoginIp"),
          value: loginInfo.data.last_login_ip_address,
        },
        {
          label: t("loginMethod"),
          value: loginMethodToString(loginInfo.data.last_login_method),
        },
        {
          label: t("userAgent"),
          value: loginInfo.data.last_login_user_agent,
        },
        {
          label: t("passwordChangedAt"),
          value: loginInfo.data.password_changed_at
            ? formatLoginTime(loginInfo.data.password_changed_at)
            : "/",
        },
      ]
    : [];

  const usedPill = (text: string) => (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        mt: 0.75,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: colors.gray100,
        color: colors.gray700,
      }}
    >
      {text}
    </Box>
  );

  const viewButtonSx = {
    ...profileButtonSx("outlined"),
    height: 30,
    px: 1.25,
    fontSize: 12.5,
  };

  const iconActionSx = {
    color: colors.primary_dark,
    "&.Mui-disabled": { color: colors.gray300 },
  };

  const header = (
    <ProfileCard sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 2, sm: 2.5 },
            flex: 1,
            minWidth: 0,
          }}
        >
          <Avatar
            sx={{
              width: { xs: 60, sm: 76 },
              height: { xs: 60, sm: 76 },
              fontSize: { xs: 22, sm: 28 },
              fontWeight: 700,
              backgroundColor: user?.disabled
                ? colors.gray400
                : colors.primary_dark,
              color: colors.white,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, sm: 24 },
                fontWeight: 700,
                lineHeight: 1.25,
                color: colors.primary_dark,
                wordBreak: "break-word",
              }}
            >
              {fullName || user?.email}
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 14,
                color: colors.gray500,
                wordBreak: "break-all",
              }}
            >
              {user?.email}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
              <StatusPill tone={user?.disabled ? "danger" : "success"}>
                {user?.disabled ? t("disabled") : t("active")}
              </StatusPill>
              {user?.workspace?.name && (
                <StatusPill tone="neutral">
                  <WorkspacesOutlined sx={{ fontSize: 15 }} />
                  {user.workspace.name}
                </StatusPill>
              )}
              {user?.is_sso && (
                <StatusPill tone="neutral">
                  <Box
                    component="img"
                    src="/microsoft_logo.png"
                    alt=""
                    sx={{ width: 13, height: 13 }}
                  />
                  {t("sso")}
                </StatusPill>
              )}
              {user && <StatusPill tone="neutral">ID {user.id}</StatusPill>}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isEditable ? (
            <>
              <Button
                onClick={handleCancelEdit}
                sx={profileButtonSx("outlined")}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleEditUser}
                disabled={editUserMutation.isPending}
                startIcon={
                  editUserMutation.isPending ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <SaveOutlined />
                  )
                }
                sx={profileButtonSx("primary")}
              >
                {t("saveChanges")}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setIsEditable(true)}
                startIcon={<EditOutlined />}
                sx={profileButtonSx("outlined")}
              >
                {t("edit")}
              </Button>
              {loginAsAdminMutation.isPending ? (
                <CircularProgress
                  size={22}
                  sx={{ color: colors.accent, mx: 1 }}
                />
              ) : (
                <Button
                  onClick={() => setLoginAsUserDialogOpen(true)}
                  startIcon={<BadgeOutlined />}
                  sx={profileButtonSx("outlined")}
                >
                  {t("loginAsUser")}
                </Button>
              )}
            </>
          )}
          <Tooltip title={t("moreActions")}>
            <IconButton
              onClick={(e) => setActionsAnchor(e.currentTarget)}
              aria-label={t("moreActions")}
              aria-haspopup="menu"
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                border: `1px solid ${colors.gray300}`,
                color: colors.primary_dark,
                "&:hover": { backgroundColor: colors.gray50 },
              }}
            >
              <MoreVertRounded />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={actionsAnchor}
            open={Boolean(actionsAnchor)}
            onClose={() => setActionsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: { ...sidebarMenuPaperSx(), mt: 0.75, minWidth: 220 },
              },
            }}
          >
            {!disableActions && (
              <MenuItem
                onClick={() => {
                  setActionsAnchor(null);
                  handleToggleDisabled();
                }}
                sx={{ gap: 1.5 }}
              >
                {user?.disabled ? (
                  <CheckCircleOutlineRounded
                    sx={{ fontSize: 20, color: colors.green700 }}
                  />
                ) : (
                  <BlockRounded
                    sx={{ fontSize: 20, color: colors.orange600 }}
                  />
                )}
                <Typography sx={{ fontSize: 14, color: colors.primary_dark }}>
                  {user?.disabled ? t("enableUser") : t("disableUser")}
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setActionsAnchor(null);
                handleConfirmationDialogOpen();
              }}
              sx={{ gap: 1.5 }}
            >
              <DeleteOutlineRounded
                sx={{ fontSize: 20, color: colors.red600 }}
              />
              <Typography sx={{ fontSize: 14, color: colors.red600 }}>
                {t("deleteUserInfo")}
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </ProfileCard>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          maxWidth: 1120,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {header}

        <ProfileCard
          title={t("accountSection")}
          icon={<PersonOutlineRounded />}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <ComoInput
              label={t("name")}
              name="name"
              value={name}
              disabled={!isEditable}
              onChange={(e) => setName(e.target.value)}
            />
            <ComoInput
              label={t("surname")}
              name="surname"
              value={surname}
              disabled={!isEditable}
              onChange={(e) => setSurname(e.target.value)}
            />
            <Tooltip
              title={isEditable && user?.is_sso ? t("ssoEmailWarning") : ""}
              arrow
            >
              <Box>
                <ComoInput
                  label={t("email")}
                  name="email"
                  value={email}
                  disabled={!isEditable || user?.is_sso}
                  onChange={handleEmailChange}
                />
              </Box>
            </Tooltip>
            {!user?.is_sso && (
              <ComoInput
                label={t("password")}
                name="password"
                value={password}
                type={showPassword ? "text" : "password"}
                disabled={!isEditable}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: isEditable && (
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? t("hidePassword") : t("showPassword")
                      }
                      sx={{ color: colors.gray500, mr: -0.5 }}
                    >
                      {showPassword ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  ),
                }}
              />
            )}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <WorkspaceSelector
                workspaces={workspacesData?.workspaces || []}
                workspaceId={workspaceId ?? ""}
                setWorkspaceId={(newWorkspaceId) => {
                  if (isEditable) {
                    applyWorkspaceDefaults.current = true;
                    setWorkspaceId(
                      typeof newWorkspaceId === "number"
                        ? newWorkspaceId
                        : null,
                    );
                  }
                }}
                disabled={!isEditable}
              />
            </Box>
          </Box>
        </ProfileCard>

        <ProfileCard title={t("limitsSection")} icon={<TuneOutlined />}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            <Box>
              <ComoInput
                label={t("evaluationLimit")}
                name="evaluation_limit"
                value={evaluationLimit}
                type="number"
                disabled={!isEditable}
                onChange={(e) => setEvaluationLimit(parseInt(e.target.value))}
              />
              {usedPill(`${t("evaluations")}: ${user?.evaluations ?? 0}`)}
            </Box>
            <Box>
              <ComoInput
                label={t("maxEntityLimit")}
                name="max_company_limit"
                value={maxCompanyLimit}
                type="number"
                disabled={!isEditable}
                onChange={(e) => setMaxCompanyLimit(parseInt(e.target.value))}
              />
              {usedPill(`${t("entities")}: ${user?.companiesCount ?? 0}`)}
            </Box>
            <ComoInput
              label={t("storageLimit")}
              name="storage_limit"
              value={storageLimit}
              type="number"
              disabled={!isEditable}
              onChange={(e) => {
                const val = e.target.value;
                setStorageLimit(val === "" ? 0 : parseInt(val));
              }}
              inputProps={{ min: 0 }}
            />
            <ComoInput
              label={t("libraryLimit")}
              name="library_limit"
              value={libraryLimit}
              type="number"
              disabled={!isEditable}
              onChange={(e) => {
                const val = e.target.value;
                setLibraryLimit(val === "" ? 0 : parseInt(val));
              }}
              inputProps={{ min: 0 }}
            />
          </Box>
        </ProfileCard>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "1fr 1fr" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <ProfileCard
            title={t("createUser:availableAiModels")}
            icon={<SmartToyOutlined />}
          >
            <ModelAccessSelector
              selectedModelIds={modelIds}
              onChange={setModelIds}
              disabled={!isEditable}
              isMobile={isMobile}
              variant="card"
            />
          </ProfileCard>
          <ProfileCard title={t("userCategories")} icon={<VerifiedOutlined />}>
            <FormControl
              component="fieldset"
              fullWidth
              margin="none"
              disabled={!isEditable}
            >
              <FormGroup
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  columnGap: 2,
                }}
              >
                {certCategories?.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={certCategory.some((c) => c.id === category.id)}
                        onChange={() => handleCertCategoryChange(category)}
                        sx={{
                          color: colors.gray400,
                          "&.Mui-checked": { color: workspace.accent },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          color: colors.primary_dark,
                          fontSize: isMobile ? "0.95rem" : "0.875rem",
                        }}
                      >
                        {category.name}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </ProfileCard>
        </Box>

        <ProfileCard>
          <FeaturesComponent
            variant="card"
            disabled={!isEditable}
            selectedFeatures={features}
            setSelectedFeatures={(updatedFeatures) => {
              if (isEditable) {
                setFeatures(updatedFeatures);
              }
            }}
          />
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Checkbox
                checked={showLockedFeatures}
                onChange={(e) => {
                  if (isEditable) setShowLockedFeatures(e.target.checked);
                }}
                disabled={!isEditable}
                sx={{
                  color: colors.gray400,
                  "&.Mui-checked": { color: workspace.accent },
                }}
              />
            }
            label={
              <Typography sx={{ color: colors.primary_dark, fontSize: 14 }}>
                {t("showLockedFeatures")}
              </Typography>
            }
          />
        </ProfileCard>

        {features.includes(Features.COMO_EXPERTS) && (
          <ProfileCard
            title={t("dashboard:dashboard.virtualTeamMembers")}
            icon={<GroupsOutlined />}
          >
            <ComoAgentSelector
              selectedAgentIds={selectedComoAgentIds}
              onChange={setSelectedComoAgentIds}
              disabled={!isEditable}
              variant="card"
            />
          </ProfileCard>
        )}

        <ProfileCard
          title={t("usersEntities")}
          subtitle={
            user?.companies?.length ? String(user.companies.length) : undefined
          }
          icon={<BusinessOutlined />}
        >
          <TableContainer sx={profileTableContainerSx()}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...profileHeadCellSx(), width: 64 }}>
                    #
                  </TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("name")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user?.companies && user.companies.length > 0 ? (
                  user.companies.map((company, index) => (
                    <TableRow key={company.id} sx={profileTableRowSx()}>
                      <TableCell sx={profileBodyCellSx()}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        {company.name}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={profileEmptyCellSx()}>
                      {t("noEntitiesAvailable")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ProfileCard>

        <ProfileCard
          title={t("userDocuments")}
          subtitle={
            user?.documents?.length ? String(user.documents.length) : undefined
          }
          icon={<DescriptionOutlined />}
        >
          <TableContainer sx={{ ...profileTableContainerSx(), maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={profileHeadCellSx()}>#</TableCell>
                  <TableCell sx={profileHeadCellSx()}>
                    {t("entityName")}
                  </TableCell>
                  <TableCell sx={profileHeadCellSx()}>
                    {t("documentName")}
                  </TableCell>
                  <TableCell sx={profileHeadCellSx()}>
                    {t("uploadDate")}
                  </TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("status")}</TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("type")}</TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("chunks")}</TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("logs")}</TableCell>
                  <TableCell sx={profileHeadCellSx()} align="right">
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user?.documents && user.documents.length > 0 ? (
                  formattedDocuments?.map((document, index) => (
                    <TableRow key={document.id} sx={profileTableRowSx()}>
                      <TableCell sx={profileBodyCellSx()}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        {companyName(document.company_id)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...profileBodyCellSx(),
                          fontWeight: 600,
                          wordBreak: "break-word",
                        }}
                      >
                        {document.name}
                      </TableCell>
                      <TableCell
                        sx={{ ...profileBodyCellSx(), whiteSpace: "nowrap" }}
                      >
                        {document.formattedDate}
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        <StatusPill tone={processingTone(document.status)}>
                          {processingStatusToString(document.status)}
                        </StatusPill>
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        {documentTypeToString(document.type)}
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        <Button
                          onClick={() => handleOpenChunks(document.id)}
                          disabled={
                            chunksLoading &&
                            chunksDocumentId === document.id &&
                            !chunksPayload
                          }
                          sx={viewButtonSx}
                        >
                          {chunksLoading && chunksDocumentId === document.id ? (
                            <CircularProgress
                              size={14}
                              sx={{ color: "inherit" }}
                            />
                          ) : (
                            t("view")
                          )}
                        </Button>
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        <Button
                          onClick={() => handleViewLogs(document.id)}
                          sx={viewButtonSx}
                        >
                          {t("view")}
                        </Button>
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()} align="right">
                        <Tooltip title={t("downloadDocument")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadDocument(
                                  document.id,
                                  document.name,
                                )
                              }
                              disabled={
                                document.status !== ProcessingStatus.PROCESSED
                              }
                              sx={iconActionSx}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} sx={profileEmptyCellSx()}>
                      {t("noDocumentsAvailable")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ProfileCard>

        <ProfileCard
          title={t("userWebsites")}
          subtitle={
            user?.websites?.length ? String(user.websites.length) : undefined
          }
          icon={<LanguageOutlined />}
        >
          <TableContainer sx={profileTableContainerSx()}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={profileHeadCellSx()}>#</TableCell>
                  <TableCell sx={profileHeadCellSx()}>
                    {t("entityName")}
                  </TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("url")}</TableCell>
                  <TableCell sx={profileHeadCellSx()}>{t("status")}</TableCell>
                  <TableCell sx={profileHeadCellSx()} align="right">
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user?.websites && user?.websites.length ? (
                  user.websites.map((website, index) => (
                    <TableRow key={website.id} sx={profileTableRowSx()}>
                      <TableCell sx={profileBodyCellSx()}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        {companyName(website.company_id)}
                      </TableCell>
                      <TableCell
                        sx={{ ...profileBodyCellSx(), wordBreak: "break-all" }}
                      >
                        <Box
                          component="a"
                          href={website.web_adress}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: colors.primary_dark,
                            fontWeight: 600,
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {website.web_adress}
                        </Box>
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()}>
                        <StatusPill tone={processingTone(website.status)}>
                          {statusToStringWebsites(website.status)}
                        </StatusPill>
                      </TableCell>
                      <TableCell sx={profileBodyCellSx()} align="right">
                        <Tooltip title={t("downloadScrapedData")}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadScrappedWebsiteContent(
                                  website.id,
                                  website.web_adress,
                                )
                              }
                              disabled={
                                website.status !== ProcessingStatus.PROCESSED
                              }
                              sx={iconActionSx}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={profileEmptyCellSx()}>
                      {t("noWebsitesAvailable")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ProfileCard>

        <ProfileCard
          title={t("evaluationLogs")}
          subtitle={
            logData?.total_count ? String(logData.total_count) : undefined
          }
          icon={<HistoryRounded />}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {logData?.user_evaluation_requests?.length ? (
              logData.user_evaluation_requests.map((req) => (
                <LogCard
                  key={req.id}
                  evaluationRequest={req}
                  showUserInfo={false}
                />
              ))
            ) : (
              <Typography
                sx={{
                  py: 2,
                  textAlign: "center",
                  fontSize: 13.5,
                  color: colors.gray500,
                }}
              >
                {t("noEvaluationLogs")}
              </Typography>
            )}
            {(logData?.total_count ?? 0) > PAGE_SIZE && (
              <TablePagination
                component="div"
                count={logData?.total_count ?? 0}
                page={page - 1}
                onPageChange={(_, p) => setPage(p + 1)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[]}
                sx={{
                  alignSelf: "center",
                  color: colors.primary_dark,
                  "& .MuiTablePagination-displayedRows": {
                    color: colors.primary_dark,
                  },
                  "& .MuiIconButton-root": { color: colors.primary_dark },
                  "& .MuiIconButton-root.Mui-disabled": {
                    color: colors.gray300,
                  },
                }}
              />
            )}
          </Box>
        </ProfileCard>

        {user && <QuestionListsTable userId={user.id} />}
        {user && <UsageWidget userId={user.id} />}

        <ProfileCard title={t("userLoginInfoTitle")} icon={<LoginRounded />}>
          {loginInfo?.data ? (
            <>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "200px minmax(0, 1fr)",
                  },
                  columnGap: 3,
                  rowGap: { xs: 0.25, sm: 1.25 },
                }}
              >
                {loginRows.map((row) => (
                  <React.Fragment key={row.label}>
                    <Box
                      component="dt"
                      sx={{
                        fontSize: 13,
                        color: colors.gray500,
                        mt: { xs: 1, sm: 0 },
                      }}
                    >
                      {row.label}
                    </Box>
                    <Box
                      component="dd"
                      sx={{
                        m: 0,
                        fontSize: 14,
                        color: colors.primary_dark,
                        wordBreak: "break-word",
                      }}
                    >
                      {row.value}
                    </Box>
                  </React.Fragment>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 2.5,
                  pt: 2.5,
                  borderTop: `1px solid ${colors.gray100}`,
                }}
              >
                <Typography sx={{ fontSize: 13, color: colors.gray500, mb: 1 }}>
                  {t("userLocation")}
                </Typography>
                {ipLoading ? (
                  <CircularProgress size={20} sx={{ color: colors.accent }} />
                ) : ipLocation ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: colors.primary_dark,
                      }}
                    >
                      {ipLocation.city}, {ipLocation.region},{" "}
                      {ipLocation.country} {ipLocation.flagEmoji}
                    </Typography>
                    <Typography
                      sx={{ mt: 0.5, fontSize: 12.5, color: colors.gray600 }}
                    >
                      {ipLocation.utc &&
                        `${t("timezone")}: ${formatUTC(ipLocation.utc)} · ISP: ${ipLocation.isp} · `}
                      {t("latitude")}: {ipLocation.latitude.toFixed(4)} ·{" "}
                      {t("longitude")}: {ipLocation.longitude.toFixed(4)}
                      {ipLocation.accuracyRadius &&
                        ` · ${t("approximateAccuracy")}: ±${ipLocation.accuracyRadius} km`}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1.5,
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: `1px solid ${colors.gray200}`,
                      }}
                    >
                      <iframe
                        title="user-location-map"
                        width="100%"
                        height="260"
                        loading="lazy"
                        style={{ border: 0, display: "block" }}
                        src={getGoogleMapEmbedUrl(
                          ipLocation.latitude,
                          ipLocation.longitude,
                        )}
                      />
                    </Box>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 14, color: colors.gray500 }}>
                    {t("locationNotAvailable")}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: colors.gray500 }}>
              {t("noLoginInfo")}
            </Typography>
          )}
        </ProfileCard>

        {isEditable && (
          <Box
            sx={{
              position: "sticky",
              bottom: 16,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              px: 2.5,
              py: 1.5,
              borderRadius: "14px",
              backgroundColor: colors.primary_dark,
              boxShadow: "0 12px 32px rgba(16, 24, 40, 0.25)",
            }}
          >
            <EditOutlined sx={{ color: colors.white, fontSize: 20 }} />
            <Typography
              sx={{
                flex: 1,
                minWidth: 180,
                fontSize: 14,
                fontWeight: 600,
                color: colors.white,
              }}
            >
              {t("editingNotice")}
            </Typography>
            <Button
              onClick={handleCancelEdit}
              sx={{
                ...profileButtonSx("outlined"),
                backgroundColor: "transparent",
                color: colors.white,
                borderColor: "rgba(255, 255, 255, 0.4)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  borderColor: colors.white,
                },
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={editUserMutation.isPending}
              startIcon={
                editUserMutation.isPending ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <SaveOutlined />
                )
              }
              sx={{
                ...profileButtonSx("primary"),
                backgroundColor: colors.accent,
                borderColor: colors.accent,
                color: colors.primary_dark,
                "&:hover": { backgroundColor: colors.accent, opacity: 0.9 },
              }}
            >
              {t("saveChanges")}
            </Button>
          </Box>
        )}

        <Dialog
          open={confirmationDialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="remove-user-title"
          aria-describedby="remove-user-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle
            id="remove-user-title"
            sx={{
              color: colors.primary_dark,
              fontSize: 18,
              fontWeight: 700,
              pt: 3,
            }}
          >
            {t("removeUserConfirmation")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id="remove-user-description"
              sx={{ color: colors.gray700, fontSize: 14.5 }}
            >
              {t("removeUserQuestion")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => handleConfirmationDialogClose(false)}
              sx={profileButtonSx("outlined")}
            >
              {t("no")}
            </Button>
            <Button
              onClick={() => handleConfirmationDialogClose(true)}
              disabled={deleteUserMutation.isPending}
              autoFocus
              sx={profileButtonSx("danger")}
            >
              {t("yes")}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmationDialog
          open={loginAsUserDialogOpen}
          onClose={() => setLoginAsUserDialogOpen(false)}
          title={t("loginAsUser")}
          content={`${t("areYouSureLoginAsUser")} ${user?.name} ${user?.surname}?`}
          description={""}
          actions={[
            {
              label: t("no"),
              onClick: () => setLoginAsUserDialogOpen(false),
            },
            {
              label: t("yes"),
              onClick: () => {
                setLoginAsUserDialogOpen(false);
                handleLoginAsUser();
              },
            },
          ]}
        />

        <Dialog
          open={chunksDialogOpen}
          onClose={handleCloseChunks}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              pt: 2.5,
              pb: 1.5,
            }}
          >
            <Typography
              component="span"
              sx={{ color: colors.primary_dark, fontSize: 18, fontWeight: 700 }}
            >
              {t("chunksDialogTitle")}
            </Typography>
            <IconButton
              onClick={handleCloseChunks}
              aria-label={t("close")}
              sx={{ color: colors.gray500 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3 }}>
            {chunksLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress sx={{ color: colors.accent }} />
              </Box>
            ) : chunksError ? (
              <DialogContentText>{t("chunksLoadError")}</DialogContentText>
            ) : chunksPayload ? (
              <Box
                sx={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  border: `1px solid ${colors.gray200}`,
                  borderRadius: "12px",
                  px: 2,
                  py: 1.5,
                  backgroundColor: colors.gray50,
                }}
              >
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    color: colors.gray900,
                  }}
                >
                  {JSON.stringify(chunksPayload, null, 2)}
                </pre>
              </Box>
            ) : (
              <DialogContentText>{t("noChunksLoaded")}</DialogContentText>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={handleCloseChunks}
              sx={profileButtonSx("outlined")}
            >
              {t("close")}
            </Button>
            <Button
              onClick={handleDownloadChunks}
              disabled={!chunksPayload}
              startIcon={<DownloadIcon />}
              sx={profileButtonSx("primary")}
            >
              {t("downloadChunks")}
            </Button>
          </DialogActions>
        </Dialog>

        <LogViewerModal
          open={logModalOpen}
          onClose={() => setLogModalOpen(false)}
          documentId={selectedDocumentId}
        />
      </Box>
    </Box>
  );
};
export default UserProfile;
